/**
 * Per-event-type registration-page defaults (2026-07-31).
 *
 * Some event types ship a LOCKED, bundled registration-page presentation — a fixed banner image
 * and fixed Event Detail copy — rather than letting the operator author it. The only variable parts
 * are merge fields pulled from the event/org record (event time, partner/org name). Range Night is
 * the first such type; more will be added later (keep this keyed by event type).
 *
 * When a type is `locked`, the public registration page (EventLandingPage) renders the banner and
 * the interpolated `eventDetailsTemplate` INSTEAD of the stored `event_details_html`, and the admin
 * Landing Page tab disables the Event Detail editor.
 */
import { EventType } from "../types/event.types";

export interface EventTypeRegistrationDefault {
  /** Absolute path to a banner served from crm-frontend/public/. */
  bannerImageUrl: string;
  /** Accessible alt text for the banner. */
  bannerAlt: string;
  /**
   * HTML Event Detail copy. Supports {{eventTime}} and {{orgName}} merge fields, interpolated at
   * render time from the event record. The event NAME is rendered separately as the page title.
   */
  eventDetailsTemplate: string;
  /** When true, this copy + banner are fixed: the public page ignores stored copy and the admin
   *  editor is read-only for this type. */
  locked: boolean;
}

const RANGE_NIGHT_DETAILS = `
<p>Join fellow U.S. LawShield members for a special Members-Only Range Night at {{orgName}}. Bring a friend, share a lane, and enjoy an evening at the range from {{eventTime}}. Arrive anytime and spend the evening shooting, connecting, and enjoying exclusive member benefits.</p>
<p>When you RSVP and attend, you&rsquo;ll receive:</p>
<br />
<ul><li>Free Range Time</li></ul>
<br />
<p><strong>Bonus Offer:</strong> Members are encouraged to bring a friend to share their lane and enjoy the experience together. Non-member guests who join U.S. LawShield will receive a $20 voucher. Existing Members who upgrade, add a spouse, or purchase EmergencyShield during the event will receive an additional $20 toward their voucher.</p>
<br />
<p>Space is limited for this event, so be sure to reserve your spot today.</p>
`.trim();

/**
 * Insurance-state Range Night copy (owner-approved 2026-10-07, migration 392; replacement block 2026-10-08).
 * Identical to RANGE_NIGHT_DETAILS except the standard "Bonus Offer" paragraph is replaced by
 * RANGE_NIGHT_INSURANCE_BONUS_BLOCK (member-benefit wording with no voucher amounts), directly under the
 * "Free Range Time" bullet. Which events get it is decided server-side by `eib_registration_variant(landing_page_id)`
 * (the event's venue state is in `eib_insurance_states`); the list is data, not code.
 */
export const RANGE_NIGHT_BONUS_OFFER_BLOCK =
  "<p><strong>Bonus Offer:</strong> Members are encouraged to bring a friend to share their lane and enjoy the experience together. Non-member guests who join U.S. LawShield will receive a $20 voucher. Existing Members who upgrade, add a spouse, or purchase EmergencyShield during the event will receive an additional $20 toward their voucher.</p>\n<br />\n";

if (!RANGE_NIGHT_DETAILS.includes(RANGE_NIGHT_BONUS_OFFER_BLOCK)) {
  // Fail loudly at load if the standard copy is edited without updating the insurance variant.
  throw new Error(
    "eventTypeRegistrationDefaults: Bonus Offer block not found in RANGE_NIGHT_DETAILS",
  );
}
/** Owner-supplied insurance-state Bonus Offer copy (2026-10-08). Mentions no voucher or dollar amount. */
export const RANGE_NIGHT_INSURANCE_BONUS_BLOCK =
  "<p><strong>Bonus Offer: More Benefits for You and Your Guest!</strong><br />Bring a friend, share a lane, and enjoy the Range Night experience together!</p>\n" +
  "<p>Thinking about upgrading? Unlock exclusive member benefits, including a Gunshot Wound Urgent Care Kit and a metal membership card, when you upgrade your membership.</p>\n" +
  "<p>Bringing a friend? If they join U.S. LawShield, they&rsquo;ll receive access to both member benefits, too!</p>\n<br />\n";

const RANGE_NIGHT_DETAILS_INSURANCE = RANGE_NIGHT_DETAILS.replace(
  RANGE_NIGHT_BONUS_OFFER_BLOCK,
  RANGE_NIGHT_INSURANCE_BONUS_BLOCK,
);

/**
 * Mig 1480 (EIB state offers, Adam's workbook column F, copy fixes approved 10-29): a Range Night whose event CTA gives
 * a voucher gets the state's Bonus Offer paragraph with the EVENT'S own amount (`eib_registration_offer`):
 *  - text A (non-insurance states): the +$20 upgrade / spouse / EmergencyShield voucher, and the guest's voucher;
 *  - text B (insurance states): the upgrade benefits (Gunshot Wound Urgent Care Kit + metal card), and the guest's
 *    voucher; Alabama adds "+ a Gunshot Wound Kit".
 */
export type RegistrationOffer = {
  variant: "offer";
  amount: number;
  insurance_state: boolean;
  gsw_kit: boolean;
};

/**
 * Registration-page copy variant for an event: 'insurance' (no voucher at an insurance venue: member-benefit copy),
 * a RegistrationOffer (the state's Bonus Offer with the event's amount), or 'standard'.
 */
export type RegistrationVariant = "standard" | "insurance" | RegistrationOffer;

/** Parses eib_registration_offer's answer. Anything unexpected -> 'insurance' (the copy that promises no amount). */
export function parseRegistrationOffer(data: unknown): RegistrationVariant {
  if (!data || typeof data !== "object") {
    return "insurance";
  }
  const d = data as Record<string, unknown>;
  if (d.variant === "standard") {
    return "standard";
  }
  if (d.variant === "offer") {
    const amount = Number(d.amount);
    if (Number.isFinite(amount) && amount > 0 && amount <= 1000) {
      return {
        variant: "offer",
        amount,
        insurance_state: d.insurance_state === true,
        gsw_kit: d.gsw_kit === true,
      };
    }
  }
  return "insurance";
}

function formatOfferAmount(amount: number): string {
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
}

/** The Bonus Offer paragraph for a Range Night with a voucher (texts A / B, Adam 2026-10-28 + fixes 10-29). */
export function rangeNightOfferBonusBlock(offer: RegistrationOffer): string {
  const amount = formatOfferAmount(offer.amount);
  const guest = offer.gsw_kit
    ? `${amount} instant voucher + a Gunshot Wound Kit`
    : `${amount} instant voucher`;
  const upgrade = offer.insurance_state
    ? "Thinking about upgrading? Unlock exclusive member benefits, including a Gunshot Wound Urgent Care Kit and a metal membership card, when you upgrade."
    : "Thinking about upgrading, adding a spouse, or adding EmergencyShield? Do it during the event and receive a $20 instant voucher.";
  return (
    `<p><strong>Bonus Offer:</strong> Bring a friend, share a lane, and enjoy the Range Night experience together! ${upgrade} ` +
    `Bringing a friend? If they join U.S. LawShield, they&rsquo;ll receive a ${guest}.</p>\n<br />\n`
  );
}

const DEFAULTS: Partial<Record<string, EventTypeRegistrationDefault>> = {
  [EventType.RANGE_NIGHT]: {
    bannerImageUrl: "/range-night-banner.jpg",
    bannerAlt: "Exclusive Member Range Event — Grab your gear and a friend.",
    eventDetailsTemplate: RANGE_NIGHT_DETAILS,
    locked: true,
  },
};

const INSURANCE_DEFAULTS: Partial<
  Record<string, EventTypeRegistrationDefault>
> = {
  [EventType.RANGE_NIGHT]: {
    ...DEFAULTS[EventType.RANGE_NIGHT]!,
    eventDetailsTemplate: RANGE_NIGHT_DETAILS_INSURANCE,
  },
};

/**
 * Returns the bundled registration-page default for an event type, or undefined if none.
 * `variant` defaults to 'standard'; pass 'insurance' (from `eib_registration_variant`) for an event in a listed
 * venue state. Types without an insurance variant return their standard default either way.
 */
export function getRegistrationDefault(
  eventType: string | null | undefined,
  variant: RegistrationVariant = "standard",
): EventTypeRegistrationDefault | undefined {
  if (!eventType) {
    return undefined;
  }
  if (variant === "insurance" && INSURANCE_DEFAULTS[eventType]) {
    return INSURANCE_DEFAULTS[eventType];
  }
  if (
    typeof variant === "object" &&
    eventType === EventType.RANGE_NIGHT &&
    DEFAULTS[eventType]
  ) {
    return {
      ...DEFAULTS[eventType]!,
      eventDetailsTemplate: RANGE_NIGHT_DETAILS.replace(
        RANGE_NIGHT_BONUS_OFFER_BLOCK,
        rangeNightOfferBonusBlock(variant),
      ),
    };
  }
  return DEFAULTS[eventType];
}

/** Interpolates {{eventTime}} / {{orgName}} in a locked template. Missing values fall back sanely. */
export function fillRegistrationTemplate(
  template: string,
  vars: { eventTime?: string | null; orgName?: string | null },
): string {
  return template
    .replaceAll("{{eventTime}}", vars.eventTime?.trim() || "the scheduled time")
    .replaceAll("{{orgName}}", vars.orgName?.trim() || "our partner range");
}
